import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  LabelList,
} from 'recharts';
import { useRevenueKpi } from '../../hooks/useRevenueKpi';
import { cappedAxisMax, axisTicks, AXIS_TARGET_CAP_MULTIPLE } from '../../utils/chartAxis';
import { MONEY_GLOSSARY } from '../../utils/moneyGlossary';
import type { RevenueKpiLabel } from '../../services/api/reports';
import {
  DATE_PLACEHOLDER,
  ENGLISH_LABEL_FORMAT,
  formatMonthYear2Digit,
} from '../../utils/dateFormat';
import { LEGACY_EN_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';
import { useReportingFxDep } from '../../hooks/useReportingFxSync';
import {
  activeMoneyCurrency,
  reportingRateForMonth,
  reportingRateLatest,
} from '../../utils/reportingMoney';

function shortMonth(key: string): string {
  // 'YYYY-MM' → "Jul 26" (MKS-351: shared util). This axis already read
  // English before the ticket (it called en-US), so it passes the English label
  // spec and reads the same in both release-flag branches.
  const label = formatMonthYear2Digit(key, ENGLISH_LABEL_FORMAT);
  return label === DATE_PLACEHOLDER ? key : label;
}

/** Compact CZK for axis ticks and stack-top total labels. */
function compact(value: number): string {
  const n = Math.round(value);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

/** Fallback year list until the first response names the real ones. */
const CURRENT_YEAR = new Date().getFullYear();

/** Neutral gray for the No-label bucket — it reads as "untagged", not as a tag. */
const NO_LABEL_COLOR = '#9ca3af'; // gray-400

/**
 * Fallback hues for labels with no `Label.color`, matching the leads
 * RevenueTimeline chart so the two revenue surfaces read alike.
 */
const FALLBACK_COLORS = [
  '#3b82f6', // blue-500
  '#22c55e', // green-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
] as const;

/** Recharts dataKey + legend name for the per-month catch-up target line. */
const TARGET_SERIES_KEY = 'target';
const TARGET_SERIES_NAME = 'Monthly target';

/**
 * MKS-210 — the two series added beside the bars. Both are LINES over the
 * stacked invoiced bars rather than extra bars: should-invoice is a superset
 * of what the bars already show (invoiced + the open queue), so drawing it as
 * a bar beside them would double-count the invoiced part to the eye.
 */
const SHOULD_INVOICE_SERIES_KEY = 'shouldInvoice';
const FORECAST_SERIES_KEY = 'forecast';
const EXTRA_SERIES_NAMES: Record<string, string> = {
  [SHOULD_INVOICE_SERIES_KEY]: 'Should invoice',
  [FORECAST_SERIES_KEY]: 'Retainer forecast',
};
const SHOULD_INVOICE_COLOR = '#0ea5e9'; // sky-500
const FORECAST_COLOR = '#64748b'; // slate-500

/** Averaging windows the forecast selector offers, in complete months. */
const FORECAST_WINDOW_OPTIONS = [1, 2, 3, 6, 12] as const;
const DEFAULT_FORECAST_WINDOW = 3;

/** Recharts dataKey for a label's stacked series. */
function seriesKey(id: number | null): string {
  return id === null ? 'label_none' : `label_${id}`;
}

export default function RevenueKpiPage() {
  // A calendar year, not a rolling window: the target resets each January, so
  // a window straddling two years would draw two unrelated catch-up curves.
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  // Empty = no filter (all labels). `null` inside selects the No-label bucket.
  const [selectedLabelIds, setSelectedLabelIds] = useState<(number | null)[]>([]);
  const [forecastWindow, setForecastWindow] = useState<number>(DEFAULT_FORECAST_WINDOW);
  /** 'YYYY-MM' of the month whose gap drill-down is open, or null. */
  const [openGapMonth, setOpenGapMonth] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useRevenueKpi(
    year,
    selectedLabelIds,
    forecastWindow
  );

  // Served by the report, so the selector can't offer a year with no data —
  // and never narrowed by the label filter.
  const availableYears = data?.availableYears ?? [CURRENT_YEAR];
  /**
   * MKS-314 follow-up — every figure on this page is CZK off the billing
   * ledger, and every one of them is attributed to a MONTH. That makes this
   * the page the per-month rule was written for: each month's revenue, target,
   * forecast and gap convert at THAT month's average rate, and the window
   * total is the sum of those conversions rather than one rate on one total.
   *
   * The conversion happens once, where the chart series is shaped, so the
   * bars, the axis, the tooltip and the KPI tiles can never disagree about
   * which rate they used. `useReportingFxDep` is the render-stable dependency
   * that makes the memo recompute when the rates arrive — the rate table
   * itself is module state and is not reactive.
   */
  const fxDep = useReportingFxDep();
  const moneyCurrency = activeMoneyCurrency();
  const czk = useMemo(
    () =>
      numberFormatter(
        { style: 'currency', currency: moneyCurrency, maximumFractionDigits: 0 },
        LEGACY_EN_NUMBER_LOCALE
      ),
    [moneyCurrency]
  );

  /**
   * `target` is the FLAT per-month share of the annual figure; each month's
   * own catch-up target lives on `months[].target`.
   *
   * MKS-314 follow-up — the annual target is CONFIGURED, not earned, so it
   * belongs to no month and converts at the newest rate in force. Leaving it
   * in CZK beside a converted window total would make the "vs. target" tile
   * compare two different currencies.
   */
  const latestRate = reportingRateLatest() ?? 1;
  const target = (data?.target ?? 0) * latestRate;
  const annualTarget = (data?.annualTarget ?? 0) * latestRate;
  const hasTarget = target > 0;
  const labels = data?.labels ?? [];
  const isFiltered = selectedLabelIds.length > 0;

  const {
    series,
    stackedLabels,
    windowTotal,
    windowInvoices,
    monthsWithData,
    fxConversionIncomplete,
  } = useMemo(() => {
    const monthsData = data?.months ?? [];
    // `void fxDep` — the rates are read through the module-level helpers
    // below, so the dependency has no other use here than forcing this memo
    // to rebuild when they land.
    void fxDep;

    /**
     * How many reporting-currency units one CZK is worth in a given month, or
     * `null` when that month has no rate. Null is NOT silently treated as 1:
     * plotting a CZK bar on a EUR axis is a wrong number, so the month's money
     * is dropped from the series and the page says so.
     */
    const rateOf = (month: string) => reportingRateForMonth(month);
    let fxConversionIncomplete = false;

    /** A money field at its own month's rate, or null when there is none. */
    const at = (month: string, value: number | null | undefined): number | null => {
      if (value === null || value === undefined) return null;
      const rate = rateOf(month);
      if (rate === null) {
        if (value !== 0) fxConversionIncomplete = true;
        return null;
      }
      return value * rate;
    };

    // Total revenue per label across the whole window — drives both which
    // series to stack and their order (largest at the bottom of the stack).
    // Converted per month before summing, so the stack order is the order in
    // the currency actually shown rather than in the ledger's.
    const totalByLabel = new Map<number | null, number>();
    for (const month of monthsData) {
      for (const slice of month.byLabel) {
        totalByLabel.set(
          slice.labelId,
          (totalByLabel.get(slice.labelId) ?? 0) + (at(month.month, slice.revenue) ?? 0)
        );
      }
    }

    const byId = new Map<number | null, RevenueKpiLabel>(
      (data?.labels ?? []).map((l) => [l.id, l])
    );
    let fallbackIndex = 0;
    const stackedLabels = [...totalByLabel.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => {
        const label = byId.get(id);
        const color =
          id === null
            ? NO_LABEL_COLOR
            : (label?.color ?? FALLBACK_COLORS[fallbackIndex++ % FALLBACK_COLORS.length]!);
        return {
          id,
          key: seriesKey(id),
          name: label?.name ?? (id === null ? 'No label' : `Label ${id}`),
          color,
        };
      });

    const series = monthsData.map((month) => {
      const row: Record<string, number | string | null> = {
        month: month.month,
        label: shortMonth(month.month),
        revenue: at(month.month, month.revenue) ?? 0,
        invoiceCount: month.invoiceCount,
        // Each month's own catch-up target, plotted as a line rather than one
        // flat reference line — it moves with the year-to-date shortfall.
        // `null` for months beyond the one in progress, which Recharts skips
        // rather than drawing a dot for.
        target: at(month.month, month.target),
        // MKS-210. Both are null outside their own half of the year, which
        // Recharts skips rather than plotting as a zero — so the two lines
        // never meet at the axis floor.
        shouldInvoice: at(month.month, month.shouldInvoice),
        forecast: at(month.month, month.forecast),
        gap: at(month.month, month.gap),
        // Transparent anchor stacked on top of every bar so the month total
        // always renders at the true top of the stack, regardless of which
        // types are present in that month.
        labelAnchor: 0,
      };
      for (const label of stackedLabels) row[label.key] = 0;
      for (const slice of month.byLabel) {
        row[seriesKey(slice.labelId)] = at(month.month, slice.revenue) ?? 0;
      }
      return row;
    });

    return {
      series,
      stackedLabels,
      // The window total is the SUM of the per-month conversions above, never
      // one rate applied to one CZK total — the whole point of the decision.
      windowTotal: series.reduce((sum, row) => sum + Number(row.revenue ?? 0), 0),
      windowInvoices: monthsData.reduce((sum, m) => sum + m.invoiceCount, 0),
      // The /mo average divides by months that actually invoiced, not by 12 —
      // a year in progress has empty months ahead that would dilute it.
      monthsWithData: monthsData.filter((m) => m.invoiceCount > 0).length,
      fxConversionIncomplete,
    };
  }, [data?.months, data?.labels, fxDep]);

  /**
   * Y-axis maximum. Recharts' data-fitted default domain would push the target
   * line off-chart whenever no month reaches it, so the axis stretches to
   * clear it — but only up to `AXIS_TARGET_CAP_MULTIPLE` times the tallest
   * bar, past which the bars would be flattened into the floor by a target
   * that is out of reach anyway. Beyond the cap the line clips; its exact
   * figure stays on the tile and in the tooltip.
   *
   * 0 = fall back to Recharts' `auto` (an empty year with no target set).
   */
  const { axisMax, targetClipped } = useMemo(() => {
    let dataMax = 0;
    let targetMax = 0;
    for (const row of series) {
      // should-invoice is a superset of revenue and the forecast can exceed
      // both, so all three are data for the purposes of fitting the axis.
      dataMax = Math.max(
        dataMax,
        Number(row.revenue) || 0,
        Number(row.shouldInvoice) || 0,
        Number(row.forecast) || 0
      );
      targetMax = Math.max(targetMax, Number(row.target) || 0);
    }
    const max = cappedAxisMax(dataMax, targetMax);
    return { axisMax: max, targetClipped: max > 0 && targetMax > max };
  }, [series]);
  const yTicks = useMemo(() => axisTicks(axisMax), [axisMax]);

  /**
   * vs. target is measured over the WHOLE window, not the latest month: the
   * latest bucket is the calendar month in progress, so its revenue is always
   * a fraction of a month's worth and a single-month comparison reads as a
   * catastrophic miss no matter how the year is going. The window target
   * pro-rates the per-month figure across the months actually shown.
   *
   * The window still ENDS in that partial month, so the comparison stays
   * mildly pessimistic — one incomplete month out of `series.length`, rather
   * than the entire basis being incomplete.
   */
  /**
   * The year's own annual target IS the comparison basis now — a calendar year
   * needs no pro-rating, which is what made the old rolling-window comparison
   * awkward.
   */
  const pctOfTarget = hasTarget ? (windowTotal / annualTarget) * 100 : null;
  const onTrack = windowTotal >= annualTarget;

  /**
   * The catch-up target of the last month that HAS one — the month in progress
   * for the current year, December for a past year. What the TARGET tile
   * shows, since "the monthly target" is no longer one number.
   */
  const latestMonthTarget = useMemo(() => {
    for (let i = series.length - 1; i >= 0; i--) {
      const value = series[i]!.target;
      if (value !== null && value !== undefined) {
        return { month: String(series[i]!.label), target: Number(value) };
      }
    }
    return null;
  }, [series]);

  /**
   * MKS-210 — the months that still owe something, and the open drill-down.
   * A month with no gap is not offered: an empty project list is a dead end.
   */
  const gapMonths = useMemo(
    () => (data?.months ?? []).filter((month) => (month.gap ?? 0) > 0),
    [data?.months]
  );
  /**
   * MKS-314 follow-up — each gap month at its OWN rate, then summed. A month
   * with no rate is dropped rather than added as CZK, which is the same rule
   * the series above follows and the reason `gapFxIncomplete` exists.
   */
  const { totalGap, gapFxIncomplete } = useMemo(() => {
    // See the series memo: the rates are module state read through the helper
    // below, so this dependency exists only to rebuild when they land.
    void fxDep;
    let total = 0;
    let incomplete = false;
    for (const month of gapMonths) {
      const rate = reportingRateForMonth(month.month);
      if (rate === null) {
        incomplete = true;
        continue;
      }
      total += (month.gap ?? 0) * rate;
    }
    return { totalGap: total, gapFxIncomplete: incomplete };
  }, [gapMonths, fxDep]);
  const openGap = useMemo(
    () => (data?.months ?? []).find((month) => month.month === openGapMonth) ?? null,
    [data?.months, openGapMonth]
  );
  /**
   * The drill-down is ONE month, so it has one rate — no sum, no blend. Falls
   * back to 1 when the month has no rate, which is the same case
   * `gapFxIncomplete` already warns about above the table.
   */
  const openGapRate = openGap ? (reportingRateForMonth(openGap.month) ?? 1) : 1;

  function toggleLabel(id: number | null): void {
    setSelectedLabelIds((prev) =>
      prev.some((selected) => selected === id)
        ? prev.filter((selected) => selected !== id)
        : [...prev, id]
    );
  }

  const selectedNames = labels
    .filter((l) => selectedLabelIds.some((id) => id === l.id))
    .map((l) => l.name);

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Monthly Revenue</h1>
          <p className="text-sm text-gray-500 mt-1">
            {year} Invoiced (CZK) &mdash; by the invoice&rsquo;s service period, not its issue
            date &mdash; by month and project label
            {hasTarget ? (
              <>
                {' '}
                vs. the {czk.format(annualTarget)} annual target, spread as a catch-up pace — a
                month short raises the months after it
              </>
            ) : null}
            . A project with several labels counts under its most specific one.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-gray-600">
            <span className="sr-only">Forecast averaging window</span>
            <select
              value={forecastWindow}
              onChange={(e) => setForecastWindow(Number(e.target.value))}
              title="How many complete months the retainer forecast averages"
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {FORECAST_WINDOW_OPTIONS.map((months) => (
                <option key={months} value={months}>
                  Forecast: last {months} mo
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            <span className="sr-only">Year</span>
            <select
              value={year}
              onChange={(e) => {
                setYear(Number(e.target.value));
                // The open drill-down belongs to a month of the old year.
                setOpenGapMonth(null);
              }}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Label filter. Empty selection = all labels, so switching the last
          chip off returns to "All labels". */}
      {labels.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase text-gray-400 mr-1">Label</span>
          <button
            type="button"
            onClick={() => setSelectedLabelIds([])}
            aria-pressed={!isFiltered}
            className={`rounded-full border px-3 py-1 text-sm transition-colors ${
              !isFiltered
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            All labels
          </button>
          {labels.map((label) => {
            const active = selectedLabelIds.some((id) => id === label.id);
            return (
              <button
                key={label.id ?? 'none'}
                type="button"
                onClick={() => toggleLabel(label.id)}
                aria-pressed={active}
                title={
                  label.id === null
                    ? 'Projects carrying no label'
                    : `${label.projectCount} project${label.projectCount === 1 ? '' : 's'}`
                }
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                  active
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{
                    backgroundColor:
                      label.id === null ? NO_LABEL_COLOR : (label.color ?? '#9ca3af'),
                  }}
                />
                {label.name}
              </button>
            );
          })}
        </div>
      )}

      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}

      {isError && (
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm text-gray-500">Couldn&apos;t load revenue.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-medium text-primary-600 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && data && (
        <>
          {isFiltered && (
            <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Showing {selectedNames.join(', ')} only. The {czk.format(annualTarget)} annual
              target covers <span className="font-medium">all</span> labels, so the vs.-target
              figure below is not comparable while a filter is active. The monthly target curve
              is unaffected — it always reads the full, unfiltered year.
            </p>
          )}

          {/* Window total + target KPI tiles */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p
                className="text-xs font-medium uppercase text-gray-400"
                title={MONEY_GLOSSARY.invoiced.definition}
              >
                {/* MKS-332: this page's headline figure IS Invoiced — it is
                    already attributed by billing-period month (the service
                    period), so only the name and the definition were missing. */}
                Invoiced · {year}
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {czk.format(windowTotal)}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {windowInvoices} invoice{windowInvoices === 1 ? '' : 's'} ·{' '}
                {czk.format(windowTotal / Math.max(1, monthsWithData))}/mo avg
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium uppercase text-gray-400">
                Target · {latestMonthTarget?.month ?? 'this month'}
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {hasTarget && latestMonthTarget ? czk.format(latestMonthTarget.target) : '—'}
              </p>
              {/* The monthly figure is no longer a constant, so name the one
                  number that IS configured and how far this month's catch-up
                  pace sits from the flat share of it. */}
              <p className="text-xs text-gray-400 mt-0.5">
                {hasTarget ? (
                  <>
                    catch-up pace · {czk.format(annualTarget)} / year ({czk.format(target)}/mo
                    flat)
                  </>
                ) : (
                  'no annual target set'
                )}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium uppercase text-gray-400">vs. target</p>
              <p
                className={`mt-1 text-2xl font-semibold ${
                  !hasTarget ? 'text-gray-400' : onTrack ? 'text-green-600' : 'text-amber-600'
                }`}
              >
                {pctOfTarget !== null ? `${pctOfTarget.toFixed(0)}%` : '—'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {hasTarget ? (
                  <>
                    {czk.format(Math.abs(windowTotal - annualTarget))}{' '}
                    {onTrack ? 'over' : 'under'} {czk.format(annualTarget)}
                  </>
                ) : (
                  'set one in admin settings'
                )}
              </p>
            </div>
            {/* MKS-210 — the gap tile. The number IS the To-Be-Invoiced
                queue across the year: billable work that has not been
                invoiced yet. */}
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium uppercase text-gray-400">Gap · not invoiced</p>
              <p
                className={`mt-1 text-2xl font-semibold ${
                  totalGap > 0 ? 'text-amber-600' : 'text-green-600'
                }`}
              >
                {czk.format(totalGap)}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {gapMonths.length > 0 ? (
                  <>
                    across {gapMonths.length} month{gapMonths.length === 1 ? '' : 's'} · pick one
                    below
                  </>
                ) : (
                  'every month is fully invoiced'
                )}
              </p>
            </div>
          </div>

          {/* MKS-210 — caveats the number itself cannot carry. */}
          {data.gapFxUnavailable && (
            <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              At least one retainer is billed in a foreign currency with no ČNB fixing available for
              its period, so its paušál is <span className="font-medium">missing</span> from the gap
              and should-invoice figures rather than converted at a guessed rate.
            </p>
          )}

          {/* MKS-314 follow-up — the reporting-currency conversion's own
              caveat, kept apart from the one above: that one is about a
              retainer's source currency, this one is about this page being
              RE-DENOMINATED for display. A month with no rate is left out
              rather than added as CZK, so the figures run short. */}
          {(fxConversionIncomplete || gapFxIncomplete) && (
            <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              No {moneyCurrency} exchange rate was available for at least one month in this window,
              so that month is <span className="font-medium">missing</span> from the figures above
              rather than counted at its koruna value.
            </p>
          )}

          {/* Monthly bars, stacked by project label, with the target reference line */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                {/* Key the chart on the stacked series set so it fully remounts
                    when the filter adds/removes labels. Recharts otherwise keeps
                    stale stack offsets and the total label anchors mid-stack. */}
                <ComposedChart
                  key={stackedLabels.map((l) => l.key).join('|')}
                  data={series}
                  margin={{ top: 24, right: 16, bottom: 8, left: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={compact}
                    width={48}
                    domain={axisMax > 0 ? [0, axisMax] : [0, 'auto']}
                    // Explicit ticks: with a numeric domain Recharts uses the
                    // bounds verbatim and does NOT re-nice its tick run.
                    ticks={axisMax > 0 ? yTicks : undefined}
                    // Required for the cap to mean anything: without it a
                    // target above the domain is drawn outside the plot area
                    // instead of being clipped at its edge.
                    allowDataOverflow={axisMax > 0}
                  />
                  <Tooltip
                    cursor={false}
                    formatter={(value, name) => {
                      if (name === TARGET_SERIES_KEY) {
                        return [czk.format(Number(value)), TARGET_SERIES_NAME];
                      }
                      const extra = EXTRA_SERIES_NAMES[String(name)];
                      if (extra) return [czk.format(Number(value)), extra];
                      const label = stackedLabels.find((l) => l.key === name);
                      return [czk.format(Number(value)), label?.name ?? String(name)];
                    }}
                    labelFormatter={(label) => String(label)}
                    contentStyle={{ fontSize: 13, borderRadius: 8 }}
                    itemSorter={(item) => -(Number(item.value) || 0)}
                  />
                  <Legend
                    formatter={(value) =>
                      value === TARGET_SERIES_KEY
                        ? TARGET_SERIES_NAME
                        : (EXTRA_SERIES_NAMES[String(value)] ??
                          stackedLabels.find((l) => l.key === value)?.name ??
                          String(value))
                    }
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  {/* The target is per-month now, so it is a SERIES rather
                      than one flat ReferenceLine.
                      NOT `stepAfter`: on a categorical axis that draws each
                      plateau BETWEEN two ticks, anchored at the left one, so a
                      month's target appears to hover over the gap after its
                      bar — December's figure read as November's. A dot per
                      month sits directly above its own bar instead, and the
                      connecting segments only join them. Omitted entirely when
                      no annual target is configured: a line pinned at 0 along
                      the axis would read as a real target. */}
                  {hasTarget && (
                    <Line
                      dataKey={TARGET_SERIES_KEY}
                      type="linear"
                      stroke="#f97316"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={{ r: 2.5, fill: '#f97316', strokeWidth: 0 }}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  )}
                  {/* MKS-210 — what the month should have billed. Solid, so
                      the dashed lines around it (target, forecast) stay the
                      "not an actual" visual language. It stops at the month
                      in progress; the dashed forecast picks up after it. */}
                  <Line
                    dataKey={SHOULD_INVOICE_SERIES_KEY}
                    type="linear"
                    stroke={SHOULD_INVOICE_COLOR}
                    strokeWidth={2}
                    dot={{ r: 2.5, fill: SHOULD_INVOICE_COLOR, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  {/* MKS-210 — the retainer projection. Dashed, and a
                      deliberately muted slate: it is the only series on the
                      chart that never happened. */}
                  <Line
                    dataKey={FORECAST_SERIES_KEY}
                    type="linear"
                    stroke={FORECAST_COLOR}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={{ r: 2.5, fill: FORECAST_COLOR, strokeWidth: 0 }}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  {stackedLabels.map((label) => (
                    <Bar
                      key={label.key}
                      dataKey={label.key}
                      stackId="revenue"
                      fill={label.color}
                      maxBarSize={64}
                    />
                  ))}
                  {/* Recharts 3 drops zero-height rectangles
                      (Bar.computeBarRectangles), which would strip this anchor
                      and its total LabelList. A custom (empty) shape sets
                      hasCustomShape, keeping the zero-height segment alive so
                      the month total renders at the true top of the stack. */}
                  <Bar
                    dataKey="labelAnchor"
                    stackId="revenue"
                    fill="transparent"
                    maxBarSize={64}
                    isAnimationActive={false}
                    legendType="none"
                    tooltipType="none"
                    shape={() => <g />}
                  >
                    <LabelList
                      dataKey="revenue"
                      position="top"
                      // Empty months would otherwise print a row of "0"s.
                      formatter={(v) => (Number(v) === 0 ? '' : compact(Number(v)))}
                      style={{ fontSize: 11, fill: '#6b7280', fontWeight: 500 }}
                    />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {/* A line that simply vanishes off the top would look like a bug,
                so say why. The exact figure is on the Target tile. */}
            {targetClipped && (
              <p className="mt-2 text-xs text-gray-400">
                The target line runs off the top in months where the catch-up target exceeds{' '}
                {AXIS_TARGET_CAP_MULTIPLE}× the year&apos;s highest month — the axis stops there
                so the bars stay readable.
              </p>
            )}
            {data.forecast && (
              <p className="mt-2 text-xs text-gray-400">
                The dashed forecast is the recurring retainer (balíček paušál) only — never
                overage, which is variable — averaged over{' '}
                {data.forecast.basisMonths.length > 0 ? (
                  <>
                    the last {data.forecast.windowMonths} complete month
                    {data.forecast.windowMonths === 1 ? '' : 's'} (
                    {data.forecast.basisMonths.join(', ')})
                  </>
                ) : (
                  'no complete months yet'
                )}
                .
              </p>
            )}
          </div>

          {/* MKS-210 — the gap drill-down. Buttons rather than a chart click
              alone: a <Bar onClick> is unreachable by keyboard, and the
              months that owe something are exactly the ones worth offering. */}
          {gapMonths.length > 0 && (
            <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-gray-900">
                Gap — billable, not yet invoiced
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                What the To-Be-Invoiced queue still holds open for each month. Pick a month to see
                the projects behind it.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {gapMonths.map((month) => {
                  const active = month.month === openGapMonth;
                  return (
                    <button
                      key={month.month}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setOpenGapMonth(active ? null : month.month)}
                      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                        active
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {shortMonth(month.month)} ·{' '}
                      {compact((month.gap ?? 0) * (reportingRateForMonth(month.month) ?? 1))}
                    </button>
                  );
                })}
              </div>

              {openGap && (
                <table className="mt-4 w-full text-sm">
                  <caption className="sr-only">
                    Projects causing the {shortMonth(openGap.month)} gap
                  </caption>
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-400">
                      <th scope="col" className="py-1 font-medium">
                        Project
                      </th>
                      <th scope="col" className="py-1 text-right font-medium">
                        Not invoiced
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {openGap.gapProjects.map((project) => (
                      <tr key={project.projectId} className="border-t border-gray-100">
                        <td className="py-1.5 text-gray-700">{project.projectName}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-900">
                          {czk.format(project.amount * openGapRate)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-200 font-medium">
                      <td className="py-1.5 text-gray-700">{shortMonth(openGap.month)} total</td>
                      <td className="py-1.5 text-right tabular-nums text-gray-900">
                        {czk.format((openGap.gap ?? 0) * openGapRate)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
